import { LitElement, html, css } from 'lit';
import { base, cards, forms } from '../styles.js';
import { formatDate } from './loan-card.js';
import './document-picker.js';
import './order-form.js';

/**
 * The two request types registered on the Partner Connect product. The value is the wire string
 * ICE carries on the transaction; the label is what the officer reads. These are OUR names -- the
 * BFF maps each to a PBG service per tenant, and no ML5 service name ever surfaces here. Trimmed
 * from five on 2026-09-29 to match the entitlement request (#742): a title review rides on the
 * ancillary type (it has no SKU of its own), the closing disclosure arrives inside the package,
 * and document review is folded into the package too. Offering a type the entitlement does not
 * cover would hand the officer a rejected order, so this list and the product's requestTypes move
 * together -- scripts/Set-EpcProductRequestTypes.ps1 is the product side. A redraw is an update to
 * the existing order, not a request type. eClose is out of scope (owner's call, 2026-09-08).
 */
export const REQUEST_TYPES = [
  { value: 'CLOSING_DOC_DELIVERY', label: 'Closing documents' },
  { value: 'ANCILLARY_DOCUMENT', label: 'Single document' },
];

export const PACKAGE_TYPES = [
  { value: 'standard', label: 'Standard — Texas purchase' },
  { value: 'heloc', label: 'Home equity (Texas 50(a)(6))' },
  { value: 'refi', label: 'Refinance' },
];

export const DEFAULT_ORDER = Object.freeze({
  requestType: 'CLOSING_DOC_DELIVERY',
  packageType: 'standard',
  rush: false,
  instructions: '',
  documents: [],
  extra: {},
});

/**
 * Screen 3 of the handoff -- "Order options & documents". The fixed F0 request form: the
 * origin's optionsSchema is always null in this phase (see OriginProduct in the BFF), so the
 * request type, package type, rush flag and special instructions are laid out here by hand.
 * A registered schema, when one arrives, adds a card of its own below rather than replacing this.
 *
 * Nothing here is sent anywhere in F0. The value is collected so F1 wires Place order to a
 * form that already exists and is already tested.
 */
export class OrderOptions extends LitElement {
  static properties = {
    documents: { type: Array },
    attachBusy: { type: Boolean },
    schema: { type: Object },
    closingDate: { type: String },
    value: { type: Object },
  };

  static styles = [base, cards, forms, css`
    :host { display: block; }
    epc-document-picker { margin-top: var(--pbg-space-gap); }
    epc-document-picker + .card { margin-top: var(--pbg-space-gap); }
  `];

  constructor() {
    super();
    this.documents = [];
    this.schema = null;
    this.closingDate = null;
    this.value = { ...DEFAULT_ORDER };
  }

  get #hasSchema() {
    return !!this.schema?.properties && Object.keys(this.schema.properties).length > 0;
  }

  get valid() {
    // A selection is required only when documents were OFFERED: an empty eFolder must not make
    // Place order unreachable (2026-09-30; the picker's own `valid` says the same and why).
    if (this.documents?.length > 0 && !(this.value.documents?.length > 0)) return false;
    if (!this.#hasSchema) return true;
    return this.shadowRoot?.querySelector('epc-order-form')?.valid ?? true;
  }

  /** Show the inline errors now, as a submit attempt does (the mock's docErr.show). */
  reportValidity() {
    this.shadowRoot?.querySelector('epc-document-picker')?.reportValidity();
    return this.valid;
  }

  render() {
    const v = this.value ?? DEFAULT_ORDER;
    const closing = formatDate(this.closingDate);
    return html`
      <div class="card">
        <div class="card-head"><span class="ch-title">Request</span></div>
        <div class="card-body">
          <div class="form-row">
            <span class="flbl" id="rqt-label">Request type <span class="req" aria-hidden="true">*</span></span>
            <div class="seg" role="radiogroup" aria-label="Request type" aria-labelledby="rqt-label">
              ${REQUEST_TYPES.map((t) => html`
                <input type="radio" name="requestType" id="rqt-${t.value}" value=${t.value}
                       .checked=${v.requestType === t.value} @change=${this.#onRequestType}>
                <label for="rqt-${t.value}">${t.label}</label>`)}
            </div>
          </div>

          <div class="form-row">
            <label class="flbl" for="pkgType">Closing package type</label>
            <select class="inp" id="pkgType" data-field="packageType" .value=${v.packageType} @change=${this.#onPackageType}>
              ${PACKAGE_TYPES.map((p) => html`<option value=${p.value} ?selected=${v.packageType === p.value}>${p.label}</option>`)}
            </select>
          </div>

          <div class="form-row">
            <span class="flbl">Handling</span>
            <label class="doc-item dashed ${v.rush ? 'on' : ''}">
              <input type="checkbox" data-field="rush" .checked=${v.rush} @change=${this.#onRush} />
              <span class="doc-meta">
                <span class="dname">Rush preparation</span>
                <span class="dsub">Prioritize ahead of standard turn time. ${closing ? `Closing is ${closing}.` : 'Closing date not set.'}</span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <epc-document-picker .documents=${this.documents} .selected=${v.documents ?? []}
                           .attachBusy=${!!this.attachBusy}
                           @documents-changed=${this.#onDocuments}></epc-document-picker>

      ${this.#hasSchema ? html`
        <div class="card">
          <div class="card-head"><span class="ch-title">Registered options</span></div>
          <div class="card-body">
            <epc-order-form .schema=${this.schema} .value=${v.extra ?? {}} @options-changed=${this.#onSchemaOptions}></epc-order-form>
          </div>
        </div>` : ''}

      <div class="card">
        <div class="card-head"><span class="ch-title">Special instructions</span></div>
        <div class="card-body">
          <label class="sr-only" for="instructions">Special instructions</label>
          <textarea class="inp" id="instructions" data-field="instructions" maxlength="2000" .value=${v.instructions}
                    placeholder="Optional — notes for the PBG preparation team (e.g. POA in use, specific vesting language)…"
                    @input=${this.#onInstructions}></textarea>
        </div>
      </div>`;
  }

  #set(patch) {
    this.value = { ...DEFAULT_ORDER, ...this.value, ...patch };
    this.dispatchEvent(new CustomEvent('options-changed', {
      detail: { value: this.value, valid: this.valid }, bubbles: true, composed: true,
    }));
  }

  #onRequestType = (e) => { if (e.target.checked) this.#set({ requestType: e.target.value }); };
  #onPackageType = (e) => this.#set({ packageType: e.target.value });
  #onRush = (e) => this.#set({ rush: e.target.checked });
  #onInstructions = (e) => this.#set({ instructions: e.target.value });
  #onDocuments = (e) => { e.stopPropagation(); this.#set({ documents: e.detail.selected }); };
  // The schema form uses the same event name; keep its detail from escaping as ours.
  #onSchemaOptions = (e) => { e.stopPropagation(); this.#set({ extra: e.detail.value }); };
}

customElements.define('epc-order-options', OrderOptions);
